import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, CircleDashed, ChevronRight, ChevronDown, X } from 'lucide-react';
import { useSupplierProfile, useSupplierTours, usePromoCodes } from '@/hooks/suppliers';
import { useBookings } from '@/hooks/bookings';
import { getItem, setItem, LS_DISMISSED_KEY, LS_EXPLORE_KEY } from '@/shared/lib/localStorage';

// ─── Types ───
interface OnboardingTask {
  id: string;
  label: string;
  route: string;
  isCompleted: boolean;
}

// ─── Helpers ───
function CircularProgress({ percent }: { percent: number }) {
  const radius = 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className="shrink-0">
      <circle cx="12" cy="12" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="3" />
      <circle
        cx="12"
        cy="12"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="text-lime-600 transition-all duration-700"
        transform="rotate(-90 12 12)"
      />
    </svg>
  );
}

// ─── Component ───
export function OnboardingWidget() {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => getItem(LS_DISMISSED_KEY) === 'true');
  const [explored, setExplored] = useState(() => getItem(LS_EXPLORE_KEY) === 'true');

  const profileResult = useSupplierProfile();
  const profile = profileResult.profile as {
    onboarding_completed?: boolean;
    first_name?: string | null;
    last_name?: string | null;
    photo_url?: string | null;
    bio?: string | null;
    phone?: string | null;
    bank_iban?: string | null;
  } | null;
  const profileLoading = profileResult.loading;
  const { tours, loading: toursLoading } = useSupplierTours(true);
  const { bookings, loading: bookingsLoading } = useBookings();
  const { promoCodes, loading: promosLoading } = usePromoCodes();

  const loading = profileLoading || toursLoading || bookingsLoading || promosLoading;

  // ── Real data checks ──
  const hasProfile = !!(
    profile?.first_name &&
    profile?.last_name &&
    (profile?.onboarding_completed ?? profile?.photo_url ?? profile?.phone)
  );
  const hasTours = !toursLoading && tours.length > 0;
  const hasBookings = !bookingsLoading && bookings.length > 0;
  const hasPayout = !!profile?.bank_iban;
  const hasPromo = !promosLoading && promoCodes.length > 0;

  // ── Tasks ──
  const tasks: OnboardingTask[] = useMemo(
    () => [
      { id: 'profile', label: 'Complete your profile', route: '/supplier/supplier-settings', isCompleted: hasProfile },
      { id: 'first-tour', label: 'Create your first tour', route: '/supplier/activities/new', isCompleted: hasTours },
      { id: 'first-booking', label: 'Get your first booking', route: '/supplier/bookings', isCompleted: hasBookings },
      { id: 'promo-code', label: 'Create a promo code', route: '/supplier/promotions', isCompleted: hasPromo },
      { id: 'payout', label: 'Set up your payout method', route: '/supplier/wallet', isCompleted: hasPayout },
      { id: 'explore', label: 'Explore the platform', route: '/supplier/dashboard', isCompleted: explored },
    ],
    [hasProfile, hasTours, hasBookings, hasPromo, hasPayout, explored]
  );

  const completedCount = tasks.filter((t) => t.isCompleted).length;
  const totalCount = tasks.length;
  const percent = Math.round((completedCount / totalCount) * 100);

  // ── Handlers ──
  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleTaskClick = useCallback(
    (route: string, taskId: string) => {
      if (taskId === 'explore') {
        setItem(LS_EXPLORE_KEY, 'true');
        setExplored(true);
      }
      setIsExpanded(false);
      void navigate(route);
    },
    [navigate]
  );

  const handleDismiss = useCallback(() => {
    setItem(LS_DISMISSED_KEY, 'true');
    setIsDismissed(true);
  }, []);

  // ── Update dismissed from other tabs ──
  useEffect(() => {
    const handle = (e: StorageEvent) => {
      if (e.key === LS_DISMISSED_KEY) setIsDismissed(e.newValue === 'true');
      if (e.key === LS_EXPLORE_KEY) setExplored(e.newValue === 'true');
    };
    window.addEventListener('storage', handle);
    return () => {
      window.removeEventListener('storage', handle);
    };
  }, []);

  if (loading) return null;
  if (isDismissed) return null;
  if (completedCount === totalCount && !isExpanded) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {/* Expanded Card */}
      <div
        className={`mb-4 w-80 origin-bottom-right md:w-96 transition-all duration-200 ${
          isExpanded ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-95 opacity-0 pointer-events-none'
        }`}
      >
        <div className="overflow-hidden rounded-[24px] bg-white border border-gray-200 shadow-lg">
          {/* Header */}
          <div
            role="button"
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleToggle();
            }}
            className="flex cursor-pointer items-center justify-between border-b p-4"
          >
            <h3 className="heading-3">Getting Started</h3>
            <ChevronDown className="h-5 w-5 text-gray-400" />
          </div>

          {/* Task List */}
          <div className="divide-y divide-gray-100">
            {tasks.map((task) => (
              <button
                key={task.id}
                type="button"
                onClick={() => {
                  handleTaskClick(task.route, task.id);
                }}
                className="group flex w-full items-center justify-between p-4 text-left transition-colors duration-200 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  {/* Icon */}
                  {task.isCompleted ? (
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                  ) : (
                    <CircleDashed className="h-5 w-5 shrink-0 text-gray-300 group-hover:text-lime-600" />
                  )}

                  {/* Label */}
                  <span
                    className={
                      task.isCompleted ? 'desc-md line-through decoration-gray-500' : 'nav-label text-gray-900'
                    }
                  >
                    {task.label}
                  </span>
                </div>

                {/* Action indicator */}
                {!task.isCompleted && (
                  <ChevronRight className="h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
                )}
              </button>
            ))}
          </div>

          {/* Dismiss footer */}
          <button
            type="button"
            onClick={handleDismiss}
            className="flex w-full items-center justify-center gap-1.5 border-t border-gray-100 px-4 py-3 nav-label-inactive hover:!text-gray-600 transition-colors hover:bg-gray-50"
          >
            <X className="h-3.5 w-3.5" />
            Don&apos;t show again
          </button>
        </div>
      </div>

      {/* Collapsed Pill */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex cursor-pointer items-center gap-2 rounded-[16px] bg-white p-3 px-4 transition-all duration-200 border active:scale-95 border-gray-200 shadow-sm"
      >
        <CircularProgress percent={percent} />
        <span className="nav-label text-gray-900">Getting Started: {percent}%</span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
    </div>
  );
}
